import { AxiosError } from "axios";
import { saveAs } from "file-saver";
import { createApi } from "../../utils/createApi";
import {
  EdgeDevice,
  EdgeDeviceProvisionTokenResponse,
  EdgeDeviceResponse,
  StagingWireguardServerResponse,
} from "./edgeDeviceInterfaces";

const getRelativeApiPath = (nextUrl: string) => {
  if (!nextUrl.startsWith("http")) {
    return nextUrl;
  }

  try {
    const parsedUrl = new URL(nextUrl);
    const apiSplit = parsedUrl.pathname.split("/api/");
    const relativePath = apiSplit[1] || "";
    return `${relativePath}${parsedUrl.search}`;
  } catch {
    return nextUrl;
  }
};

export const fetchAllEdgeDevices = async (): Promise<EdgeDevice[]> => {
  const AxiosUI = createApi("");
  const allDevices: EdgeDevice[] = [];
  const visitedUrls = new Set<string>();
  let nextUrl: string | null = "edge_devices/";

  while (nextUrl) {
    const relativeUrl = getRelativeApiPath(nextUrl);
    if (visitedUrls.has(relativeUrl)) {
      break;
    }

    visitedUrls.add(relativeUrl);
    const response = await AxiosUI.get<EdgeDeviceResponse>(relativeUrl);

    allDevices.push(...(response.data?.results ?? []));
    nextUrl = response.data?.next ?? null;
  }

  return allDevices;
};

export const parseProvisionTokenError = (reason: unknown): string => {
  const err = reason as AxiosError<{ error?: string; detail?: string }>;
  const message =
    typeof err.response?.data === "object" && err.response?.data !== null
      ? (err.response.data as { error?: string; detail?: string }).error ??
        (err.response.data as { error?: string; detail?: string }).detail
      : typeof err.response?.data === "string"
        ? err.response.data
        : null;
  return message ?? "Failed to get edge device provision token.";
};

export const requestEdgeDeviceProvisionToken = async (payload: {
  serial_number: string;
  mac_address: string;
}): Promise<EdgeDeviceProvisionTokenResponse> => {
  const AxiosUI = createApi("");
  const response = await AxiosUI.post<EdgeDeviceProvisionTokenResponse>(
    "edge_devices/provision-token/",
    payload
  );
  return response.data;
};

export const fetchStagingWireguardServers =
  async (): Promise<StagingWireguardServerResponse | null> => {
    const AxiosUI = createApi("");
    const response = await AxiosUI.get<StagingWireguardServerResponse>(
      "edge_devices/staging-wireguard-server/"
    );
    const data = response.data;
    if (!data || typeof data.wireguard_server !== "string") {
      return null;
    }
    return { ...data, pool: Array.isArray(data.pool) ? data.pool : [] };
  };

export const downloadEdgeDeviceBootstrapZip = async (): Promise<void> => {
  const AxiosUI = createApi("");
  const response = await AxiosUI.get("edge_devices/bootstrap-scripts/", {
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: "application/zip" });
  saveAs(blob, "edge_device_bootstrap.zip");
};
